import React, { useState, useMemo } from 'react';
import './BacktestTab.css';

// Official prize tables (excluding jackpot which is variable)
const POWERBALL_PRIZES = {
  '5+1': null,       // Jackpot
  '5+0': 1_000_000,
  '4+1': 50_000,
  '4+0': 100,
  '3+1': 100,
  '3+0': 7,
  '2+1': 7,
  '1+1': 4,
  '0+1': 4,
};

const MEGA_PRIZES = {
  '5+1': null,       // Jackpot
  '5+0': 1_000_000,
  '4+1': 10_000,
  '4+0': 500,
  '3+1': 200,
  '3+0': 10,
  '2+1': 10,
  '1+1': 4,
  '0+1': 2,
};

const POWERBALL_ROWS = [
  { key: '5+1', label: '5 + Powerball', jackpot: true },
  { key: '5+0', label: '5 numbers' },
  { key: '4+1', label: '4 + Powerball' },
  { key: '4+0', label: '4 numbers' },
  { key: '3+1', label: '3 + Powerball' },
  { key: '3+0', label: '3 numbers' },
  { key: '2+1', label: '2 + Powerball' },
  { key: '1+1', label: '1 + Powerball' },
  { key: '0+1', label: 'Powerball only' },
];

const MEGA_ROWS = [
  { key: '5+1', label: '5 + Mega Ball', jackpot: true },
  { key: '5+0', label: '5 numbers' },
  { key: '4+1', label: '4 + Mega Ball' },
  { key: '4+0', label: '4 numbers' },
  { key: '3+1', label: '3 + Mega Ball' },
  { key: '3+0', label: '3 numbers' },
  { key: '2+1', label: '2 + Mega Ball' },
  { key: '1+1', label: '1 + Mega Ball' },
  { key: '0+1', label: 'Mega Ball only' },
];

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

export default function BacktestTab({ game, draws, freq, lastSeen, getBallClass }) {
  const [selectedMain, setSelectedMain]   = useState([]);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [results, setResults]             = useState(null);

  const prizes   = game.id === 'powerball' ? POWERBALL_PRIZES : MEGA_PRIZES;
  const prizeRows = game.id === 'powerball' ? POWERBALL_ROWS   : MEGA_ROWS;

  const toggleMain = (n) => {
    setResults(null);
    setSelectedMain(prev => {
      if (prev.includes(n)) return prev.filter(x => x !== n);
      if (prev.length >= game.mainCount) return prev;
      return [...prev, n].sort((a, b) => a - b);
    });
  };

  const toggleBonus = (n) => {
    setResults(null);
    setSelectedBonus(prev => prev === n ? null : n);
  };

  const clearAll = () => {
    setSelectedMain([]);
    setSelectedBonus(null);
    setResults(null);
  };

  const canRun = selectedMain.length === game.mainCount && selectedBonus !== null;

  const runBacktest = () => {
    if (!canRun || !draws.length) return;

    const breakdown = {};
    let totalWon = 0;
    let jackpots = 0;
    let anyWin   = 0;

    draws.forEach(draw => {
      const mainMatches  = selectedMain.filter(n => draw.main.includes(n)).length;
      const bonusMatch   = draw.bonus === selectedBonus ? 1 : 0;
      const key          = `${mainMatches}+${bonusMatch}`;
      breakdown[key]     = (breakdown[key] || 0) + 1;

      const prize = prizes[key];
      if (prize === null) { jackpots++; anyWin++; }
      else if (prize > 0) { totalWon += prize; anyWin++; }
    });

    setResults({
      breakdown,
      totalWon,
      totalSpent: draws.length * 2,
      jackpots,
      anyWin,
    });
  };

  // Normalise freq range so we can colour-code picked numbers
  const freqRange = useMemo(() => {
    const vals = Object.values(freq).filter(v => v > 0);
    if (!vals.length) return { min: 0, range: 1 };
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    return { min, range: max - min || 1 };
  }, [freq]);

  const getStatus = (n) => {
    const f   = freq[n] || 0;
    const pct = (f - freqRange.min) / freqRange.range;
    if (pct >= 0.75) return { label: 'Hot',    cls: 'hot'     };
    if (pct >= 0.45) return { label: 'Warm',   cls: 'warm'    };
    if (pct >= 0.20) return { label: 'Cool',   cls: 'neutral' };
    return               { label: 'Cold',   cls: 'cold'    };
  };

  const firstDate = draws[0]?.date;

  return (
    <div className="bt-wrap">
      <div className="bt-intro">
        <h3 className="bt-title">Backtest My Numbers</h3>
        <p className="bt-sub">
          Pick your {game.mainCount} numbers + {game.bonusName}, then see how they would have performed
          across all {draws.length.toLocaleString()} historical {game.name} draws.
        </p>
      </div>

      {/* Main numbers grid */}
      <div className="bt-section">
        <div className="bt-section-label">
          Pick {game.mainCount} main numbers
          <span className="bt-counter">{selectedMain.length}/{game.mainCount}</span>
        </div>
        <div className="bt-grid bt-grid-main">
          {Array.from({ length: game.maxMain }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              className={`bt-num ${selectedMain.includes(n) ? 'selected' : ''}`}
              onClick={() => toggleMain(n)}
              disabled={!selectedMain.includes(n) && selectedMain.length >= game.mainCount}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Bonus number grid */}
      <div className="bt-section">
        <div className="bt-section-label">
          Pick 1 {game.bonusName}
          <span className="bt-counter">{selectedBonus !== null ? '1/1' : '0/1'}</span>
        </div>
        <div className="bt-grid bt-grid-bonus">
          {Array.from({ length: game.maxBonus }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              className={`bt-num bt-num-bonus ${selectedBonus === n ? 'selected' : ''}`}
              onClick={() => toggleBonus(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="bt-controls">
        <button className="bt-run-btn" onClick={runBacktest} disabled={!canRun}>
          {canRun ? 'Run Backtest' : `Select ${game.mainCount - selectedMain.length > 0 ? `${game.mainCount - selectedMain.length} more number${game.mainCount - selectedMain.length > 1 ? 's' : ''}` : game.bonusName}`}
        </button>
        {(selectedMain.length > 0 || selectedBonus !== null) && (
          <button className="bt-clear-btn" onClick={clearAll}>Clear</button>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="bt-results">

          {/* Summary cards */}
          <div className="bt-summary">
            <div className="bt-sum-card">
              <div className="bt-sum-val">{draws.length.toLocaleString()}</div>
              <div className="bt-sum-label">Draws analyzed</div>
            </div>
            <div className="bt-sum-card">
              <div className="bt-sum-val">{results.anyWin.toLocaleString()}</div>
              <div className="bt-sum-label">Winning tickets</div>
            </div>
            <div className="bt-sum-card accent">
              <div className="bt-sum-val">{fmtMoney(results.totalWon)}</div>
              <div className="bt-sum-label">Theoretical winnings</div>
            </div>
            <div className="bt-sum-card danger">
              <div className="bt-sum-val">{fmtMoney(results.totalSpent)}</div>
              <div className="bt-sum-label">Total spent ($2/draw)</div>
            </div>
          </div>

          {results.jackpots > 0 && (
            <div className="bt-jackpot-alert">
              🎉 Your numbers would have hit the jackpot {results.jackpots} time{results.jackpots !== 1 ? 's' : ''}!
            </div>
          )}

          <div className="bt-roi">
            Net result: <strong className={results.totalWon >= results.totalSpent ? 'bt-positive' : 'bt-negative'}>
              {results.totalWon >= results.totalSpent ? '+' : ''}{fmtMoney(results.totalWon - results.totalSpent)}
            </strong>
            {firstDate && (
              <span className="bt-roi-note">
                {' '}playing every draw since {new Intl.DateTimeFormat('en-US').format(new Date(firstDate + 'T12:00:00'))}
              </span>
            )}
          </div>

          {/* Match breakdown table */}
          <div className="bt-section-label" style={{ marginTop: '1.5rem' }}>Match breakdown</div>
          <table className="bt-table">
            <thead>
              <tr>
                <th>Match</th>
                <th>Prize</th>
                <th>Times</th>
                <th>Total won</th>
              </tr>
            </thead>
            <tbody>
              {prizeRows.map(row => {
                const count = results.breakdown[row.key] || 0;
                const prize = prizes[row.key];
                return (
                  <tr key={row.key} className={count > 0 ? 'bt-row-hit' : ''}>
                    <td>{row.label}</td>
                    <td className="bt-td-prize">
                      {prize === null ? 'Jackpot' : prize > 0 ? fmtMoney(prize) : '—'}
                    </td>
                    <td className="bt-td-count">{count > 0 ? count.toLocaleString() : '—'}</td>
                    <td className="bt-td-won">
                      {count === 0 ? '—'
                        : prize === null ? '🎉 Jackpot!'
                        : fmtMoney(prize * count)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Your numbers profile */}
          <div className="bt-section-label" style={{ marginTop: '1.5rem' }}>Your numbers — historical profile</div>
          <div className="bt-profiles">
            {selectedMain.map(n => {
              const { label, cls } = getStatus(n);
              return (
                <div key={n} className={`bt-profile-card ${cls}`}>
                  <div className="bt-profile-num">{n}</div>
                  <div className="bt-profile-freq">{(freq[n] || 0).toLocaleString()}×</div>
                  <div className={`bt-profile-status ${cls}`}>{label}</div>
                  <div className="bt-profile-gap">{lastSeen[n] ?? 0} draws ago</div>
                </div>
              );
            })}
          </div>

          <p className="bt-disclaimer">
            Historical frequency does not predict future results. Lottery draws are independent random events. For entertainment only.
          </p>
        </div>
      )}
    </div>
  );
}
